import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Chip } from 'primereact/chip';
import { RadioButton } from 'primereact/radiobutton';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateForApi, formatDateWithTime } from '../../../../utils/dateUtils';
import {
    useGetLmsCreateRequestByIdQuery,
    useGetTrainingTypesByVehicleModelQuery,
    useGetVehicleModelsWithUpcomingPlanningsQuery,
    useGetLmsCreateRequestPlanningsQuery,
    useGetLmsVehicleTrainingMatchesQuery,
    useGetLmsLookupTranslationsQuery,
    useGetUserByDealerDropdownQuery
} from '../../../../api';
import LmsCodeDropdown from '../../components/LmsCodeDropdown';
import useLmsCodeOptions from '../../hooks/useLmsCodeOptions';
import useLmsLanguageOptions from '../../hooks/useLmsLanguageOptions';
import UserDropdown from '../../../../components/common/UserDropdown';
import ExpandableNameList from './ExpandableNameList';
import TechnicianUserDropdown from './TechnicianUserDropdown';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import { REQUEST_TYPE, REQUEST_STATUS, STATUS_ACTIONS } from '../utils/requestStatus';
import useEndDatePreview from '../hooks/useEndDatePreview';

// Onayla/İade/Reddet görünümü — tender-request/components/TenderRequestForm.jsx ile aynı desen
// (o da warranty-claims/createEditForm/component/FormWarrantyClaims.jsx'teki paylaşılan
// src/components/ButtonGroup bileşenini kullanır) — kullanıcı isteği üzerine buraya da uygulandı.
const REQUEST_ACTION_BUTTON_OVERRIDES = {
    [REQUEST_STATUS.PENDING_APPROVAL]: { severity: 'success', icon: 'pi pi-send' },
    [REQUEST_STATUS.ACCEPTED]: { severity: 'success', icon: 'pi pi-check' },
    [REQUEST_STATUS.RETURNED]: { severity: 'gray', icon: 'pi pi-arrow-left' },
    [REQUEST_STATUS.REJECTED]: { severity: 'danger', icon: 'pi pi-times' }
};

// "Tüm Talepler" sekmesinin oluştur/düzenle modalı — bkz. docs/talep_olustur_onay_akısı.md:
// "editleme için modal açıldığında modalın en üstünde bir radio buton olsun, iki seçenekli olarak
// yayınlanan eğitimler ve talep edilen eğitimler seçimi yapılsın. Buna göre içerik hangi eğitim
// modalı olacaksa o dolacak şekilde değişsin."
//
// Bu bileşen `PublishedTrainingRequestForm.jsx` ve `TrainingRequestForm.jsx`'in KENDİSİNİ
// kullanmaz (ikisi de kendi başına tam bir Dialog) — bilinçli olarak, her iki formun alan/doğrulama/
// özet mantığını tek bir Dialog içinde, üstteki radio'ya göre değişen bir gövde olarak yeniden
// üretir. Mevcut iki sekme ve formları BUNDAN etkilenmez, hiçbir şekilde değiştirilmedi.

const toRequestDateTime = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T09:00:00`;
};

const formatDisplayDate = (date) => (date ? date.toLocaleDateString('tr-TR') : '');

const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

let rowKeySeq = 0;
const nextRowKey = () => `row-${++rowKeySeq}`;
const emptyTechnicianRow = () => ({ rowKey: nextRowKey(), userId: null, duty: null });

// İki formun alan kümesinin birleşimi (superset) — hangi alanların dolu/anlamlı olduğu, aşağıdaki
// `requestType`'a göre değişir. numberOfTechnician/technicians yalnızca Talep Edilen (REQUESTED)
// modunda anlamlı — bkz. TrainingRequestForm.jsx'teki eşleniği.
const emptyFormData = () => ({
    id: null,
    vehicleModel: null,
    trainingType: null,
    planningId: null,
    numberOfParticipants: null,
    participants: [],
    numberOfTechnician: null,
    technicians: [],
    trainingDayCount: null,
    preferTrainingDate: null,
    alternativeDate: null,
    trainingEndDate: null,
    alternativeEndDate: null,
    location: null,
    trainingLanguage: '',
    description: '',
    commitmentApproval: 0,
    status: 0,
    isActive: 1
});

const AllRequestsForm = ({ visible, onHide, onSubmit, initialData, loading, handleDelete, onStatusAction, readOnly = false }) => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const isEdit = !!(initialData && (initialData.requestId || initialData.id));
    const editId = isEdit ? initialData.requestId ?? initialData.id : null;

    const { data: detailData, isLoading: isLoadingDetail } = useGetLmsCreateRequestByIdQuery(editId, { skip: !isEdit || !visible });
    const sourceData = isEdit ? detailData || initialData : null;

    // Onayla/Red/İade — bkz. AllRequestsTable.jsx'in (kaldırılan) satır ikonlarıyla aynı statü
    // seti; tender-request/components/TenderRequestForm.jsx'teki allowedTenderActions ile aynı desen.
    const allowedRequestActions = useMemo(() => {
        const allowed = sourceData?.allowedStatuses;
        return Array.isArray(allowed) ? STATUS_ACTIONS.filter((action) => allowed.includes(action.targetStatus)) : [];
    }, [sourceData]);

    // Doküman: canEdit taslak/iade durumunda true, canDelete sadece taslakta true. Kayıt henüz
    // GetById'den gelmediyse (ör. detay yüklenmeden önce) listedeki `initialData` değeri baz alınır.
    const rawCanEdit = sourceData?.canEdit ?? initialData?.canEdit;
    const canEditRecord = !readOnly && (!isEdit || rawCanEdit !== false);
    const rawCanDelete = sourceData?.canDelete ?? initialData?.canDelete;
    const canDeleteRecord = isEdit && !!handleDelete && rawCanDelete === true;

    const [requestType, setRequestType] = useState(REQUEST_TYPE.PUBLISHED);
    const [formData, setFormData] = useState(emptyFormData());
    const [localSubmitted, setLocalSubmitted] = useState(false);

    // --- Yayınlanan Eğitimler moduna özgü sorgular (bkz. PublishedTrainingRequestForm.jsx) ---
    const isPublishedMode = requestType === REQUEST_TYPE.PUBLISHED;
    const { data: publishedVehicleModelsData, isFetching: isLoadingPublishedVehicleModels } = useGetVehicleModelsWithUpcomingPlanningsQuery(undefined, { skip: !visible || !isPublishedMode });
    const publishedVehicleModelOptions = useMemo(() => {
        const options = (publishedVehicleModelsData || []).map((item) => ({ value: item.vehicleModel, label: item.vehicleModelText || item.vehicleModel }));
        if (isPublishedMode && formData.vehicleModel && !options.some((opt) => opt.value === formData.vehicleModel)) {
            options.push({ value: formData.vehicleModel, label: formData.vehicleModel });
        }
        return options;
    }, [publishedVehicleModelsData, isPublishedMode, formData.vehicleModel]);

    const { data: trainingTypesByVehicleModel, isFetching: isLoadingPublishedTrainingTypes } = useGetTrainingTypesByVehicleModelQuery(formData.vehicleModel, {
        skip: !visible || !isPublishedMode || !formData.vehicleModel
    });
    const publishedTrainingTypeOptions = useMemo(() => (trainingTypesByVehicleModel || []).map((item) => ({ value: item.trainingType, label: item.trainingTypeText || item.trainingType })), [trainingTypesByVehicleModel]);

    const { data: planningsData, isFetching: isLoadingPlannings } = useGetLmsCreateRequestPlanningsQuery(
        { vehicleModel: formData.vehicleModel, trainingType: formData.trainingType },
        { skip: !visible || !isPublishedMode || !formData.vehicleModel || !formData.trainingType }
    );
    const planningOptions = useMemo(() => {
        const items = planningsData || [];
        return items
            .filter((p) => p.startDate)
            .map((p) => ({
                value: p.planningId,
                label: `${formatDateWithTime(p.startDate, false)} — ${p.planningLocationText || p.planningLocation}${p.trainingLanguage ? ' · ' + p.trainingLanguage : ''}`,
                raw: p
            }));
    }, [planningsData]);
    const selectedPlanning = useMemo(() => planningOptions.find((opt) => opt.value === formData.planningId)?.raw || null, [planningOptions, formData.planningId]);
    const preconditionRows = (selectedPlanning ?? planningOptions[0]?.raw)?.preconditions || [];

    const [fallbackPlanningInfo, setFallbackPlanningInfo] = useState(null);
    const [planningAutoMatchDone, setPlanningAutoMatchDone] = useState(false);

    // --- Talep Edilen Eğitimler moduna özgü sorgular (bkz. TrainingRequestForm.jsx) ---
    const isRequestedMode = requestType === REQUEST_TYPE.REQUESTED;
    const { languageOptions, isLoadingLanguages } = useLmsLanguageOptions();

    const { data: requestedVehicleModelsData } = useLmsCodeOptions('VEHICLE_MODEL', { skip: !visible || !isRequestedMode });
    const { data: requestedTrainingTypesData } = useLmsCodeOptions('TRAINING_TYPES', { skip: !visible || !isRequestedMode });
    const { data: requestedTrainingLocationsData } = useLmsCodeOptions('TRAINING_LOCATION', { skip: !visible || !isRequestedMode });
    const { data: requestedVehicleTrainingMatches, isFetching: isLoadingRequestedTrainingTypes } = useGetLmsVehicleTrainingMatchesQuery({ vehicleCode: formData.vehicleModel }, { skip: !visible || !isRequestedMode || !formData.vehicleModel });
    const requestedTrainingTypeOptions = useMemo(() => (requestedVehicleTrainingMatches || []).map((item) => ({ value: item.trainingCode, label: item.trainingName })), [requestedVehicleTrainingMatches]);

    const { data: technicianDutiesData, isLoading: isLoadingDuties } = useGetLmsLookupTranslationsQuery({ groupCode: 'TECHNICIAN_DUTY', isActive: 1, pageNumber: 1, pageSize: 100 }, { skip: !visible || !isRequestedMode });
    const dutyOptions = useMemo(() => (technicianDutiesData?.items || []).map((item) => ({ value: item.code, label: item.text })), [technicianDutiesData]);

    // Özet ekranında teknisyen adını göstermek için (TechnicianUserDropdown aynı sorguyu kendi
    // içinde yapıyor ama seçili kullanıcının adını dışarı vermiyor) — bkz. TrainingRequestForm.jsx.
    const { data: dealerUsersResponse } = useGetUserByDealerDropdownQuery(undefined, { skip: !visible || !isRequestedMode });
    const userNameById = useMemo(() => {
        const map = new Map();
        (dealerUsersResponse?.data || []).forEach((user) => map.set(user.id, user.name));
        return map;
    }, [dealerUsersResponse]);

    // --- Kayıt yüklendiğinde formu doldur (hem alanlar hem tespit edilen requestType) ---
    useEffect(() => {
        if (!visible) return;
        if (isEdit && !sourceData) return;

        if (!sourceData) {
            setFormData(emptyFormData());
            setRequestType(REQUEST_TYPE.PUBLISHED);
            setFallbackPlanningInfo(null);
            setPlanningAutoMatchDone(false);
            setLocalSubmitted(false);
            return;
        }

        const detectedType = sourceData.requestType === REQUEST_TYPE.REQUESTED ? REQUEST_TYPE.REQUESTED : REQUEST_TYPE.PUBLISHED;
        setRequestType(detectedType);
        setFormData({
            id: sourceData.requestId ?? sourceData.id ?? null,
            vehicleModel: sourceData.vehicleModel ?? null,
            trainingType: sourceData.trainingType ?? null,
            planningId: null, // planlama eşleştirme efektiyle doldurulur (yalnızca Yayınlanan modunda)
            numberOfParticipants: sourceData.numberOfParticipants ?? null,
            participants: (sourceData.participants || []).map((p) => ({ id: p.participantId, name: p.participantFullName, displayValue: `${p.participantId} - ${p.participantFullName}` })),
            numberOfTechnician: sourceData.numberOfTechnician ?? null,
            technicians: (sourceData.technicians || []).map((t) => ({ rowKey: nextRowKey(), userId: t.userId ?? null, duty: t.duty ?? null })),
            preferTrainingDate: sourceData.preferTrainingDate ? new Date(sourceData.preferTrainingDate) : null,
            alternativeDate: sourceData.alternativeDate ? new Date(sourceData.alternativeDate) : null,
            trainingDayCount: sourceData.trainingDayCount ?? null,
            trainingEndDate: sourceData.trainingEndDate ? new Date(sourceData.trainingEndDate) : null,
            alternativeEndDate: sourceData.alternativeEndDate ? new Date(sourceData.alternativeEndDate) : null,
            location: sourceData.locations?.[0]?.locationCode ?? null,
            trainingLanguage: sourceData.trainingLanguage ?? '',
            description: sourceData.description ?? '',
            commitmentApproval: sourceData.commitmentApproval ?? 0,
            status: sourceData.status ?? 0,
            isActive: sourceData.isActive ?? 1
        });
        setFallbackPlanningInfo({
            preferTrainingDate: sourceData.preferTrainingDate ? new Date(sourceData.preferTrainingDate) : null,
            locationText: sourceData.locations?.[0]?.locationText ?? sourceData.locations?.[0]?.locationCode ?? '',
            trainingLanguage: sourceData.trainingLanguage ?? ''
        });
        setPlanningAutoMatchDone(false);
        setLocalSubmitted(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, isEdit, sourceData]);

    // Katılımcı Sayısı — Katılımcılar alanından otomatik hesaplanır (her iki modda ortak).
    useEffect(() => {
        setFormData((prev) => (prev.participants.length === (prev.numberOfParticipants || 0) ? prev : { ...prev, numberOfParticipants: prev.participants.length || null }));
    }, [formData.participants]);

    // Teknisyen Sayısı — Teknisyenler alanından otomatik hesaplanır (yalnızca Talep Edilen modunda
    // kullanılıyor, Yayınlanan modunda liste hep boş kalır).
    useEffect(() => {
        setFormData((prev) => (prev.technicians.length === (prev.numberOfTechnician || 0) ? prev : { ...prev, numberOfTechnician: prev.technicians.length || null }));
    }, [formData.technicians]);

    // Bitiş tarihi önizlemesi yalnızca Talep Edilen modunda (Yayınlanan'da süre planlamadan gelir).
    const isCalculatingEndDate = useEndDatePreview({
        enabled: visible && isRequestedMode && canEditRecord && !isLoadingDetail,
        preferTrainingDate: toRequestDateTime(formData.preferTrainingDate),
        alternativeDate: toRequestDateTime(formData.alternativeDate),
        trainingDayCount: formData.trainingDayCount,
        setFormData
    });

    const updateTechnicianRow = (rowKey, patch) => setFormData((prev) => ({ ...prev, technicians: prev.technicians.map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)) }));
    const addTechnicianRow = () => setFormData((prev) => ({ ...prev, technicians: [...prev.technicians, emptyTechnicianRow()] }));
    const removeTechnicianRow = (rowKey) => setFormData((prev) => ({ ...prev, technicians: prev.technicians.filter((row) => row.rowKey !== rowKey) }));

    // Düzenleme modunda, planlama listesi yüklendiğinde kayıttaki tarih+lokasyonla eşleşen
    // alternatifi otomatik seçmeyi dener (yalnızca Yayınlanan modunda, yalnızca bir kez).
    useEffect(() => {
        if (!isEdit || !isPublishedMode || planningAutoMatchDone || isLoadingPlannings || !planningOptions.length || !fallbackPlanningInfo?.preferTrainingDate) return;
        const targetDateStr = formatDateForApi(fallbackPlanningInfo.preferTrainingDate);
        const match = planningOptions.find((opt) => opt.raw.startDate && formatDateForApi(new Date(opt.raw.startDate)) === targetDateStr);
        if (match) {
            setFormData((prev) => (prev.planningId ? prev : { ...prev, planningId: match.value }));
        }
        setPlanningAutoMatchDone(true);
    }, [isEdit, isPublishedMode, planningAutoMatchDone, isLoadingPlannings, planningOptions, fallbackPlanningInfo]);

    // Talep türü değiştirildiğinde (radio) — türe özgü alanlar sıfırlanır, ortak alanlar
    // (katılımcılar/açıklama/taahhüt) korunur.
    const handleTypeChange = (newType) => {
        if (newType === requestType) return;
        setRequestType(newType);
        setFormData((prev) => ({
            ...prev,
            vehicleModel: null,
            trainingType: null,
            planningId: null,
            trainingDayCount: null,
            preferTrainingDate: null,
            alternativeDate: null,
            trainingEndDate: null,
            alternativeEndDate: null,
            location: null,
            trainingLanguage: ''
        }));
        setFallbackPlanningInfo(null);
        setPlanningAutoMatchDone(false);
        setLocalSubmitted(false);
    };

    // NOT: React state güncellemeleri (setLocalSubmitted) senkron değildir — bkz. orijinal
    // formlardaki aynı not. Asıl geçerlilik kararı `localSubmitted`'dan bağımsız saf fonksiyonlarla
    // veriliyor; isFieldInvalid yalnızca kırmızı çerçeve gösterimi için.
    const checkPublishedFieldInvalid = (field) => {
        if (field === 'numberOfParticipants') return !(Number(formData.numberOfParticipants) > 0);
        if (field === 'participants') return !(formData.participants?.length > 0);
        if (field === 'planningId') return !formData.planningId;
        if (field === 'commitmentApproval') return Number(formData.commitmentApproval) !== 1;
        return formData[field] == null;
    };

    const checkRequestedFieldInvalid = (field) => {
        if (field === 'numberOfParticipants') return !(Number(formData.numberOfParticipants) > 0);
        if (field === 'participants') return !(formData.participants?.length > 0);
        if (field === 'preferTrainingDate') return !formData.preferTrainingDate || formData.preferTrainingDate < startOfToday();
        if (field === 'alternativeDate') return !!formData.alternativeDate && formData.alternativeDate < startOfToday();
        if (field === 'commitmentApproval') return Number(formData.commitmentApproval) !== 1;
        if (typeof formData[field] === 'string') return !formData[field].trim();
        return formData[field] == null;
    };

    const checkFieldInvalid = (field) => (isPublishedMode ? checkPublishedFieldInvalid(field) : checkRequestedFieldInvalid(field));
    const isFieldInvalid = (field) => {
        if (!localSubmitted) return false;
        return checkFieldInvalid(field);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canEditRecord) return;
        setLocalSubmitted(true);

        let missingRequired;
        let otherInvalid;
        let payload;

        if (isPublishedMode) {
            const requiredFields = ['vehicleModel', 'trainingType', 'planningId'];
            missingRequired = requiredFields.some((field) => checkPublishedFieldInvalid(field));
            otherInvalid = checkPublishedFieldInvalid('numberOfParticipants') || checkPublishedFieldInvalid('participants') || checkPublishedFieldInvalid('commitmentApproval');

            if (!missingRequired && !otherInvalid) {
                payload = {
                    requestType: REQUEST_TYPE.PUBLISHED,
                    vehicleModel: formData.vehicleModel,
                    trainingType: formData.trainingType,
                    numberOfParticipants: Number(formData.numberOfParticipants),
                    preferTrainingDate: selectedPlanning ? toRequestDateTime(new Date(selectedPlanning.startDate)) : null,
                    alternativeDate: null,
                    trainingLanguage: (selectedPlanning?.trainingLanguage || '').trim(),
                    description: (formData.description || '').trim(),
                    commitmentApproval: Number(formData.commitmentApproval),
                    status: Number(formData.status),
                    isActive: Number(formData.isActive),
                    locationCodes: selectedPlanning?.planningLocation ? [selectedPlanning.planningLocation] : [],
                    preconditionCodes: preconditionRows.map((row) => Number(row.preconditionCode)),
                    participantIds: formData.participants.map((p) => p.id)
                };
            }
        } else {
            const requiredFields = ['vehicleModel', 'trainingType', 'trainingLanguage', 'location'];
            missingRequired = requiredFields.some((field) => checkRequestedFieldInvalid(field));
            otherInvalid =
                checkRequestedFieldInvalid('numberOfParticipants') ||
                checkRequestedFieldInvalid('participants') ||
                checkRequestedFieldInvalid('preferTrainingDate') ||
                checkRequestedFieldInvalid('alternativeDate') ||
                checkRequestedFieldInvalid('commitmentApproval');

            if (!missingRequired && !otherInvalid) {
                const nonEmptyTechnicians = formData.technicians.filter((row) => row.userId || row.duty);
                payload = {
                    requestType: REQUEST_TYPE.REQUESTED,
                    vehicleModel: formData.vehicleModel,
                    trainingType: formData.trainingType,
                    numberOfParticipants: Number(formData.numberOfParticipants),
                    preferTrainingDate: toRequestDateTime(formData.preferTrainingDate),
                    alternativeDate: toRequestDateTime(formData.alternativeDate),
                    trainingDayCount: formData.trainingDayCount != null && formData.trainingDayCount !== '' ? Number(formData.trainingDayCount) : null,
                    trainingLanguage: formData.trainingLanguage.trim(),
                    description: (formData.description || '').trim(),
                    commitmentApproval: Number(formData.commitmentApproval),
                    status: Number(formData.status),
                    isActive: Number(formData.isActive),
                    locationCodes: formData.location ? [formData.location] : [],
                    preconditionCodes: [],
                    participantIds: formData.participants.map((p) => p.id),
                    numberOfTechnician: nonEmptyTechnicians.length || null,
                    technicians: nonEmptyTechnicians.map((row) => ({ userId: row.userId, duty: row.duty }))
                };
            }
        }

        if (missingRequired || otherInvalid) {
            toast.current?.show({ severity: 'error', summary: th('trainingRequest.missingField'), detail: th('trainingRequest.formValidationMessage'), life: 4000 });
            return;
        }

        if (isEdit) payload.id = formData.id;
        onSubmit(payload);
    };

    const textByCode = (list, code) => (list?.items || []).find((item) => item.code === code)?.text || code || '';
    const participantNames = formData.participants.map((p) => p.name);
    const dutyLabelByCode = (code) => dutyOptions.find((opt) => opt.value === code)?.label || code || '';
    const technicianNames = formData.technicians
        .filter((row) => row.userId)
        .map((row) => `${userNameById.get(row.userId) || row.userId}${row.duty ? ' (' + dutyLabelByCode(row.duty) + ')' : ''}`);

    // --- Özet paneli (moda göre) ---
    const publishedSummaryVehicleModel = publishedVehicleModelOptions.find((opt) => opt.value === formData.vehicleModel)?.label || formData.vehicleModel || '';
    const publishedSummaryTrainingType = publishedTrainingTypeOptions.find((opt) => opt.value === formData.trainingType)?.label || formData.trainingType || '';
    const publishedSummaryPreconditions =
        preconditionRows
            .map((row) => row.preconditionText)
            .filter(Boolean)
            .join(', ') || th('trainingRequest.summary.none');
    const publishedSummaryLocation = selectedPlanning?.planningLocationText || fallbackPlanningInfo?.locationText || th('trainingRequest.summary.none');
    const publishedSummaryLanguage = selectedPlanning?.trainingLanguage || fallbackPlanningInfo?.trainingLanguage || th('trainingRequest.summary.none');
    const publishedSummaryDate = selectedPlanning?.startDate
        ? new Date(selectedPlanning.startDate).toLocaleDateString('tr-TR')
        : fallbackPlanningInfo?.preferTrainingDate
        ? fallbackPlanningInfo.preferTrainingDate.toLocaleDateString('tr-TR')
        : th('trainingRequest.summary.none');
    const publishedSummaryFee = (() => {
        if (!selectedPlanning) return th('trainingRequest.summary.none');
        if (selectedPlanning.isPaid === false) return th('trainingRequest.summary.feeFree');
        return `${selectedPlanning.fee} ${selectedPlanning.currency || ''}`.trim();
    })();

    const requestedSummaryVehicleModel = textByCode(requestedVehicleModelsData, formData.vehicleModel);
    const requestedSummaryTrainingType = textByCode(requestedTrainingTypesData, formData.trainingType);
    const requestedSummaryLocation = textByCode(requestedTrainingLocationsData, formData.location);
    const renderFooter = () => (
        <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full">
            <div>{canDeleteRecord && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" type="button" onClick={() => handleDelete(formData.id)} disabled={loading} />}</div>
            <div className="flex align-items-center gap-2">
                {isEdit && onStatusAction && allowedRequestActions.length > 0 && (
                    <ButtonGroup
                        buttons={allowedRequestActions.map((action) => {
                            const override = REQUEST_ACTION_BUTTON_OVERRIDES[action.targetStatus] || {};
                            return {
                                label: th(`trainingRequest.allRequests.actions.${action.i18nKey}`),
                                icon: override.icon || action.icon,
                                severity: override.severity || 'success',
                                disabled: loading,
                                onClick: () => onStatusAction(sourceData, action.targetStatus)
                            };
                        })}
                    />
                )}
                <Button label={canEditRecord ? th('common.cancel') : th('common.close')} icon="pi pi-times" onClick={onHide} outlined type="button" />
                {canEditRecord && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={!!loading} />}
            </div>
        </div>
    );

    const isFormReady = !isEdit || !!sourceData;
    const showNoTrainingTypesMessage = isPublishedMode && !!formData.vehicleModel && !isLoadingPublishedTrainingTypes && publishedTrainingTypeOptions.length === 0;
    const showNoPlanningsMessage = isPublishedMode && !!formData.trainingType && !isLoadingPlannings && planningOptions.length === 0;
    const showEditMismatchHint = isPublishedMode && isEdit && planningAutoMatchDone && !formData.planningId && !!fallbackPlanningInfo?.preferTrainingDate;

    return (
        <Dialog
            visible={visible}
            footer={renderFooter()}
            style={{ width: '65vw', maxWidth: '900px' }}
            header={isEdit ? th('trainingRequest.allRequests.editTitle') : th('trainingRequest.allRequests.addTitle')}
            modal
            className="p-fluid"
            onHide={onHide}
            key={formData.id ?? 'new'}
        >
            <Toast ref={toast} />
            {!isFormReady || isLoadingDetail ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="p-fluid">
                    {isEdit && !canEditRecord && (
                        <div className="p-2 mb-3 border-round surface-100 text-sm text-color-secondary">
                            <i className="pi pi-info-circle mr-2" />
                            {th('trainingRequest.allRequests.viewOnlyHint')}
                        </div>
                    )}

                    <div className="field mb-4">
                        <label className="block mb-2">{th('trainingRequest.allRequests.typeSectionLabel')}</label>
                        <div className="flex flex-wrap align-items-center gap-4">
                            <div className="flex align-items-center gap-2">
                                <RadioButton inputId="requestTypePublished" name="requestType" value={REQUEST_TYPE.PUBLISHED} checked={isPublishedMode} onChange={(e) => handleTypeChange(e.value)} disabled={!canEditRecord} />
                                <label htmlFor="requestTypePublished" className="cursor-pointer">
                                    {th('trainingRequest.tabs.published')}
                                </label>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <RadioButton inputId="requestTypeRequested" name="requestType" value={REQUEST_TYPE.REQUESTED} checked={isRequestedMode} onChange={(e) => handleTypeChange(e.value)} disabled={!canEditRecord} />
                                <label htmlFor="requestTypeRequested" className="cursor-pointer">
                                    {th('trainingRequest.tabs.requested')}
                                </label>
                            </div>
                        </div>
                    </div>

                    {isPublishedMode ? (
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="allReqPublishedVehicleModel">
                                        {th('trainingRequest.fields.vehicleModel')} <span className="text-red-500">*</span>
                                    </label>
                                    <Dropdown
                                        id="allReqPublishedVehicleModel"
                                        value={formData.vehicleModel || null}
                                        options={publishedVehicleModelOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setFormData((prev) => (e.value === prev.vehicleModel ? prev : { ...prev, vehicleModel: e.value, trainingType: null, planningId: null }))}
                                        placeholder={th('trainingRequest.fields.selectPlaceholder')}
                                        loading={isLoadingPublishedVehicleModels}
                                        disabled={!canEditRecord}
                                        filter
                                        showClear
                                        className={isFieldInvalid('vehicleModel') ? 'p-invalid' : ''}
                                    />
                                    {showNoTrainingTypesMessage && <small className="text-orange-500">{th('trainingRequest.published.noTrainingTypesForModel')}</small>}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="allReqPublishedTrainingType">
                                        {th('trainingRequest.fields.trainingType')} <span className="text-red-500">*</span>
                                    </label>
                                    <Dropdown
                                        id="allReqPublishedTrainingType"
                                        value={formData.trainingType || null}
                                        options={publishedTrainingTypeOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setFormData((prev) => ({ ...prev, trainingType: e.value, planningId: null }))}
                                        placeholder={formData.vehicleModel ? th('trainingRequest.fields.selectPlaceholder') : th('trainingRequest.fields.selectVehicleModelFirstPlaceholder')}
                                        disabled={!canEditRecord || !formData.vehicleModel}
                                        loading={isLoadingPublishedTrainingTypes}
                                        filter
                                        showClear
                                        className={isFieldInvalid('trainingType') ? 'p-invalid' : ''}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-8">
                                <div className="field">
                                    <label htmlFor="allReqPublishedParticipants">
                                        {th('trainingRequest.fields.participants')} <span className="text-red-500">*</span>
                                    </label>
                                    <UserDropdown
                                        value={formData.participants}
                                        onChange={(value) => setFormData((prev) => ({ ...prev, participants: value }))}
                                        placeholder={th('trainingRequest.fields.participantsPlaceholder')}
                                        className={isFieldInvalid('participants') ? 'p-invalid' : ''}
                                        disabled={!canEditRecord}
                                        dealerScoped
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="allReqPublishedNumberOfParticipants">
                                        {th('trainingRequest.fields.numberOfParticipants')} <span className="text-red-500">*</span>
                                    </label>
                                    <InputNumber id="allReqPublishedNumberOfParticipants" value={formData.numberOfParticipants} min={1} disabled className={isFieldInvalid('numberOfParticipants') ? 'p-invalid' : ''} />
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="field">
                                    <label>{th('trainingRequest.fields.preconditions')}</label>
                                    <small className="block text-color-secondary mb-2">{th('trainingRequest.published.preconditionsHint')}</small>
                                    {!formData.trainingType ? (
                                        <div className="text-color-secondary text-sm">
                                            <i className="pi pi-info-circle mr-2" />
                                            {th('trainingRequest.published.preconditionsSelectFirstMessage')}
                                        </div>
                                    ) : isLoadingPlannings ? (
                                        <i className="pi pi-spin pi-spinner" />
                                    ) : preconditionRows.length === 0 ? (
                                        <div className="text-color-secondary text-sm">{th('trainingRequest.published.preconditionsEmptyMessage')}</div>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {preconditionRows.map((row) => (
                                                <Chip key={row.preconditionCode} label={row.preconditionText || row.preconditionCode} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="allReqPublishedPlanningId">
                                        {th('trainingRequest.fields.preferTrainingDate')} <span className="text-red-500">*</span>
                                    </label>
                                    <Dropdown
                                        id="allReqPublishedPlanningId"
                                        value={formData.planningId}
                                        options={planningOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setFormData((prev) => ({ ...prev, planningId: e.value }))}
                                        placeholder={formData.trainingType ? th('trainingRequest.published.selectDatePlaceholder') : th('trainingRequest.published.selectTrainingTypeFirstPlaceholder')}
                                        disabled={!canEditRecord || !formData.trainingType}
                                        loading={isLoadingPlannings}
                                        className={isFieldInvalid('planningId') ? 'p-invalid' : ''}
                                    />
                                    {showNoPlanningsMessage && <small className="text-orange-500">{th('trainingRequest.published.noUpcomingPlannings')}</small>}
                                    {showEditMismatchHint && <small className="text-orange-500">{th('trainingRequest.published.editPlanningMismatchHint')}</small>}
                                </div>
                            </div>

                            <div className="col-12 md:col-3">
                                <div className="field">
                                    <label>{th('trainingRequest.fields.location')}</label>
                                    <InputText value={publishedSummaryLocation === th('trainingRequest.summary.none') ? '' : publishedSummaryLocation} disabled />
                                </div>
                            </div>

                            <div className="col-12 md:col-3">
                                <div className="field">
                                    <label>{th('trainingRequest.fields.trainingLanguage')}</label>
                                    <InputText value={publishedSummaryLanguage === th('trainingRequest.summary.none') ? '' : publishedSummaryLanguage} disabled />
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="field">
                                    <label htmlFor="allReqPublishedDescription">{th('trainingRequest.fields.description')}</label>
                                    <InputTextarea
                                        id="allReqPublishedDescription"
                                        value={formData.description}
                                        rows={3}
                                        autoResize
                                        maxLength={1000}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                        placeholder={th('trainingRequest.fields.descriptionPlaceholder')}
                                        disabled={!canEditRecord}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="allReqRequestedVehicleModel">
                                        {th('trainingRequest.fields.vehicleModel')} <span className="text-red-500">*</span>
                                    </label>
                                    <LmsCodeDropdown
                                        id="allReqRequestedVehicleModel"
                                        groupCode="VEHICLE_MODEL"
                                        value={formData.vehicleModel}
                                        onChange={(value) => setFormData((prev) => (value === prev.vehicleModel ? prev : { ...prev, vehicleModel: value, trainingType: null }))}
                                        placeholder={th('trainingRequest.fields.selectPlaceholder')}
                                        invalid={isFieldInvalid('vehicleModel')}
                                        disabled={!canEditRecord}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="allReqRequestedTrainingType">
                                        {th('trainingRequest.fields.trainingType')} <span className="text-red-500">*</span>
                                    </label>
                                    <Dropdown
                                        id="allReqRequestedTrainingType"
                                        value={formData.trainingType || null}
                                        options={requestedTrainingTypeOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setFormData((prev) => ({ ...prev, trainingType: e.value }))}
                                        placeholder={formData.vehicleModel ? th('trainingRequest.fields.selectPlaceholder') : th('trainingRequest.fields.selectVehicleModelFirstPlaceholder')}
                                        disabled={!canEditRecord || !formData.vehicleModel}
                                        loading={isLoadingRequestedTrainingTypes}
                                        filter
                                        showClear
                                        className={isFieldInvalid('trainingType') ? 'p-invalid' : ''}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-8">
                                <div className="field">
                                    <label htmlFor="allReqRequestedParticipants">
                                        {th('trainingRequest.fields.participants')} <span className="text-red-500">*</span>
                                    </label>
                                    <UserDropdown
                                        value={formData.participants}
                                        onChange={(value) => setFormData((prev) => ({ ...prev, participants: value }))}
                                        placeholder={th('trainingRequest.fields.participantsPlaceholder')}
                                        className={isFieldInvalid('participants') ? 'p-invalid' : ''}
                                        disabled={!canEditRecord}
                                        dealerScoped
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="allReqRequestedNumberOfParticipants">
                                        {th('trainingRequest.fields.numberOfParticipants')} <span className="text-red-500">*</span>
                                    </label>
                                    <InputNumber id="allReqRequestedNumberOfParticipants" value={formData.numberOfParticipants} min={1} disabled className={isFieldInvalid('numberOfParticipants') ? 'p-invalid' : ''} />
                                </div>
                            </div>

                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="allReqRequestedNumberOfTechnician">{th('trainingRequest.fields.numberOfTechnician')}</label>
                                    <InputNumber id="allReqRequestedNumberOfTechnician" value={formData.numberOfTechnician} min={1} disabled />
                                </div>
                            </div>

                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="allReqRequestedTrainingDayCount">{th('trainingRequest.fields.trainingDayCount')}</label>
                                    <InputNumber
                                        id="allReqRequestedTrainingDayCount"
                                        value={formData.trainingDayCount}
                                        min={1}
                                        onValueChange={(e) => setFormData((prev) => ({ ...prev, trainingDayCount: e.value }))}
                                        disabled={!canEditRecord}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="allReqRequestedPreferTrainingDate">
                                        {th('trainingRequest.fields.preferTrainingDate')} <span className="text-red-500">*</span>
                                    </label>
                                    <Calendar
                                        id="allReqRequestedPreferTrainingDate"
                                        value={formData.preferTrainingDate}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, preferTrainingDate: e.value }))}
                                        dateFormat="dd.mm.yy"
                                        minDate={startOfToday()}
                                        showIcon
                                        disabled={!canEditRecord}
                                        className={isFieldInvalid('preferTrainingDate') ? 'p-invalid' : ''}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="allReqRequestedAlternativeDate">{th('trainingRequest.fields.alternativeDate')}</label>
                                    <Calendar
                                        id="allReqRequestedAlternativeDate"
                                        value={formData.alternativeDate}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, alternativeDate: e.value }))}
                                        dateFormat="dd.mm.yy"
                                        minDate={startOfToday()}
                                        showIcon
                                        showClear
                                        disabled={!canEditRecord}
                                        className={isFieldInvalid('alternativeDate') ? 'p-invalid' : ''}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label>{th('trainingRequest.fields.trainingEndDate')}</label>
                                    <InputText value={isCalculatingEndDate ? '...' : formatDisplayDate(formData.trainingEndDate)} disabled readOnly />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label>{th('trainingRequest.fields.alternativeEndDate')}</label>
                                    <InputText value={isCalculatingEndDate ? '...' : formatDisplayDate(formData.alternativeEndDate)} disabled readOnly />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="allReqRequestedLocation">
                                        {th('trainingRequest.fields.location')} <span className="text-red-500">*</span>
                                    </label>
                                    <LmsCodeDropdown
                                        id="allReqRequestedLocation"
                                        groupCode="TRAINING_LOCATION"
                                        value={formData.location}
                                        onChange={(value) => setFormData((prev) => ({ ...prev, location: value }))}
                                        placeholder={th('trainingRequest.fields.selectPlaceholder')}
                                        invalid={isFieldInvalid('location')}
                                        disabled={!canEditRecord}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="allReqRequestedTrainingLanguage">
                                        {th('trainingRequest.fields.trainingLanguage')} <span className="text-red-500">*</span>
                                    </label>
                                    <Dropdown
                                        id="allReqRequestedTrainingLanguage"
                                        value={formData.trainingLanguage || null}
                                        options={languageOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setFormData((prev) => ({ ...prev, trainingLanguage: e.value }))}
                                        placeholder={th('trainingRequest.fields.selectPlaceholder')}
                                        loading={isLoadingLanguages}
                                        disabled={!canEditRecord}
                                        filter
                                        showClear
                                        className={isFieldInvalid('trainingLanguage') ? 'p-invalid' : ''}
                                    />
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="field">
                                    <label htmlFor="allReqRequestedDescription">{th('trainingRequest.fields.description')}</label>
                                    <InputTextarea
                                        id="allReqRequestedDescription"
                                        value={formData.description}
                                        rows={3}
                                        autoResize
                                        maxLength={1000}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                        placeholder={th('trainingRequest.fields.descriptionPlaceholder')}
                                        disabled={!canEditRecord}
                                    />
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="surface-50 border-round p-3">
                                    <div className="flex align-items-center mb-2">
                                        <h3 className="m-0" style={{ fontSize: '1rem' }}>
                                            {th('trainingRequest.fields.technicians')}
                                        </h3>
                                        {canEditRecord && (
                                            <Button type="button" label={th('trainingRequest.fields.addTechnician')} icon="pi pi-plus" text style={{ marginLeft: 'auto', flex: '0 0 auto', width: 'auto' }} onClick={addTechnicianRow} />
                                        )}
                                    </div>
                                    {formData.technicians.length === 0 && <div className="text-color-secondary text-sm">{th('trainingRequest.fields.noRows')}</div>}
                                    {formData.technicians.map((row) => (
                                        <div key={row.rowKey} className="surface-0 border-round p-3 mb-2 border-1 surface-border">
                                            <div className="grid align-items-start">
                                                <div className="col-12 md:col-5">
                                                    <label>{th('trainingRequest.fields.technician')}</label>
                                                    <TechnicianUserDropdown
                                                        value={row.userId}
                                                        onChange={(value) => updateTechnicianRow(row.rowKey, { userId: value })}
                                                        placeholder={th('trainingRequest.fields.technicianPlaceholder')}
                                                        dealerScoped
                                                        disabled={!canEditRecord}
                                                    />
                                                </div>
                                                <div className="col-12 md:col-5">
                                                    <label>{th('trainingRequest.fields.duty')}</label>
                                                    <Dropdown
                                                        value={row.duty}
                                                        options={dutyOptions}
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        onChange={(e) => updateTechnicianRow(row.rowKey, { duty: e.value })}
                                                        placeholder={th('trainingRequest.fields.selectPlaceholder')}
                                                        loading={isLoadingDuties}
                                                        filter
                                                        showClear
                                                        disabled={!canEditRecord}
                                                    />
                                                </div>
                                                {canEditRecord && (
                                                    <div className="col-12 md:col-2 flex align-items-end justify-content-end" style={{ minHeight: '3.5rem' }}>
                                                        <Button type="button" icon="pi pi-trash" severity="danger" text onClick={() => removeTechnicianRow(row.rowKey)} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="surface-100 border-round p-3 mt-2">
                        <h3 className="mt-0 mb-1" style={{ fontSize: '1rem' }}>
                            {th('trainingRequest.summary.title')}
                        </h3>
                        <p className="text-color-secondary mt-0 mb-3" style={{ fontSize: '0.85rem' }}>
                            {th('trainingRequest.summary.hint')}
                        </p>
                        {isPublishedMode ? (
                            <div className="grid text-sm">
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.vehicleModel')}:</strong> {publishedSummaryVehicleModel || th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.trainingType')}:</strong> {publishedSummaryTrainingType || th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.numberOfParticipants')}:</strong> {formData.numberOfParticipants ?? th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.participants')}:</strong> <ExpandableNameList names={participantNames} emptyLabel={th('trainingRequest.summary.none')} />
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.preconditions')}:</strong> {publishedSummaryPreconditions}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.preferTrainingDate')}:</strong> {publishedSummaryDate}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.trainingDay')}:</strong> {selectedPlanning?.trainingDay || th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.location')}:</strong> {publishedSummaryLocation}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.trainingLanguage')}:</strong> {publishedSummaryLanguage}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.fee')}:</strong> {publishedSummaryFee}
                                </div>
                                <div className="col-12">
                                    <strong>{th('trainingRequest.summary.description')}:</strong> {formData.description || th('trainingRequest.summary.none')}
                                </div>
                            </div>
                        ) : (
                            <div className="grid text-sm">
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.vehicleModel')}:</strong> {requestedSummaryVehicleModel || th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.trainingType')}:</strong> {requestedSummaryTrainingType || th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.numberOfParticipants')}:</strong> {formData.numberOfParticipants ?? th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.participants')}:</strong> <ExpandableNameList names={participantNames} emptyLabel={th('trainingRequest.summary.none')} />
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.numberOfTechnician')}:</strong> {formData.numberOfTechnician ?? th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.technicians')}:</strong> <ExpandableNameList names={technicianNames} emptyLabel={th('trainingRequest.summary.none')} />
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.preferTrainingDate')}:</strong> {formData.preferTrainingDate ? formData.preferTrainingDate.toLocaleDateString('tr-TR') : th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.alternativeDate')}:</strong> {formData.alternativeDate ? formData.alternativeDate.toLocaleDateString('tr-TR') : th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.trainingDayCount')}:</strong> {formData.trainingDayCount ?? th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.trainingEndDate')}:</strong> {formatDisplayDate(formData.trainingEndDate) || th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.alternativeEndDate')}:</strong> {formatDisplayDate(formData.alternativeEndDate) || th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.location')}:</strong> {requestedSummaryLocation || th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12 md:col-6">
                                    <strong>{th('trainingRequest.summary.trainingLanguage')}:</strong> {formData.trainingLanguage || th('trainingRequest.summary.none')}
                                </div>
                                <div className="col-12">
                                    <strong>{th('trainingRequest.summary.description')}:</strong> {formData.description || th('trainingRequest.summary.none')}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="field-checkbox mt-3">
                        <Checkbox
                            inputId="allReqCommitmentApproval"
                            checked={Number(formData.commitmentApproval) === 1}
                            onChange={(e) => setFormData((prev) => ({ ...prev, commitmentApproval: e.checked ? 1 : 0 }))}
                            className={isFieldInvalid('commitmentApproval') ? 'p-invalid' : ''}
                            disabled={!canEditRecord}
                        />
                        <label htmlFor="allReqCommitmentApproval" className="ml-2 cursor-pointer">
                            {th('trainingRequest.commitment.label')} <span className="text-red-500">*</span>
                        </label>
                    </div>
                </form>
            )}
        </Dialog>
    );
};

export default AllRequestsForm;
